/**
 * Custom fetcher for Orval that uses raw fetch() against the API URL.
 *
 * Handles JWT injection from the token store and builds
 * the full URL from VITE_API_URL environment variable.
 */

import { posthog } from 'posthog-js';
import { tokenStore } from '@/shared/auth/token-store';
import { ValidationError } from '@/shared/form';
import {
  ApiErrorResponse,
  AuthenticationError,
  BlockedStageTransitionError,
  BlockedSubStageTransitionError,
  CouponAlreadyAppliedError,
  DiscountStackingConflictError,
  DuplicateCustomerError,
  MatrixAssociationConflictError,
  NeedsOptInError,
  PaymentMethodRequiredError,
  ReopenError,
  RoNotConvertibleError,
  TierUpgradeRequiredError,
  parseRoNotConvertibleReason,
} from './errors';
import type {
  BlockedStageTransitionCode,
  BlockedSubStageTransitionCode,
  MatrixAssociationGranularity,
  NeedsOptInLastConsentStatus,
  ReopenErrorCode,
} from './errors';
import type { ExistingCustomerInfo } from './errors';

const REOPEN_ERROR_CODES = new Set<ReopenErrorCode>([
  'RO_NOT_REOPENABLE',
  'REOPEN_NOT_ALLOWED_PAID',
  'INVALID_REOPEN_TARGET',
]);

const BLOCKED_STAGE_TRANSITION_CODES = new Set<BlockedStageTransitionCode>([
  'CLOSED_TO_ANY',
  'INVOICE_TO_EARLIER',
  'IN_PROGRESS_TO_INTAKE',
  'MUST_USE_CLOSE_ROUTE',
  'MUST_BE_APPROVED_TO_START',
]);

const BLOCKED_SUB_STAGE_TRANSITION_CODES = new Set<BlockedSubStageTransitionCode>([
  'INTAKE_APPROVED_LOCKED',
  'INTAKE_DECLINED_LOCKED',
  'SUB_STAGE_REQUIRES_INVOICE',
  'SUB_STAGE_APPROVED_REQUIRES_APPROVED_JOB',
  // Phase 4B
  'SUB_STAGE_REQUIRES_ACTIVE_DVI',
  'SUB_STAGE_REQUIRES_COMPLETED_DVI',
  'SUB_STAGE_REQUIRES_ESTIMATE_SENT',
  'SUB_STAGE_DECLINED_REQUIRES_DECLINED_JOB',
  'SUB_STAGE_REQUIRES_PENDING_PARTS_ORDER',
  'SUB_STAGE_REQUIRES_PENDING_ADDON',
  'SUB_STAGE_REQUIRES_SUBLET',
  'SUB_STAGE_PICKUP_REQUIRES_PAYMENT_OR_PICKEDUP',
]);

export const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Unauthenticated auth endpoints. A 401 from these is a domain response
 * ("invalid credentials", "invalid or expired reset token") — NOT a sign the
 * caller's session expired. They must skip the token-refresh/logout machinery
 * below: otherwise a customer submitting an expired password-reset link is
 * silently logged out and bounced to /login instead of seeing the inline
 * "request a new reset email" error. Protected auth endpoints (e.g.
 * /auth/change-password/*) are intentionally absent — a 401 there genuinely
 * does mean the access token expired and a refresh is the right response.
 */
const UNAUTHENTICATED_PATHS = new Set([
  '/auth/login',
  '/auth/signup',
  '/auth/google',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/check-identity',
]);

function isUnauthenticatedPath(url: string): boolean {
  return UNAUTHENTICATED_PATHS.has(url.split('?')[0]);
}

interface ErrorBody {
  error?: string;
  message?: string;
  fields?: Record<string, string[]>;
  existingCustomer?: ExistingCustomerInfo;
}

interface RefreshResponse {
  accessToken: string;
}

/**
 * Outcome of a token refresh attempt.
 *
 * Distinguishing between genuine auth failures and transient infrastructure
 * errors is critical: every API lambda deploy forces a cold start on the next
 * invocation, and a cold-starting lambda can briefly return 5xx / network
 * errors from `/auth/refresh`. If we treat those as an auth failure the user
 * is logged out on every backend deploy.
 */
type RefreshResult = 'success' | 'auth-failed' | 'transient';

// Exponential backoff for transient refresh failures — total ~5s bridges a
// typical Lambda cold start after a code update.
const REFRESH_RETRY_DELAYS_MS = [500, 1500, 3000];

let refreshPromise: Promise<RefreshResult> | null = null;

async function attemptSingleRefresh(mode: 'bootstrap' | 'rotate'): Promise<RefreshResult> {
  // The refresh token + sessionId live in an HttpOnly `tt_refresh` cookie
  // that the browser auto-sends with credentials: 'include' (security issue
  // #157). No JS-accessible storage to read, no body fields to send.
  //
  // `mode: 'bootstrap'` re-issues an access token without rotating the
  // session — used on first page load to hydrate the in-memory token, and
  // safe across parallel tabs / E2E workers that share one cookie.
  // `mode: 'rotate'` rotates the session — used when the access token has
  // genuinely expired and we want fresh refresh credentials.
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(mode === 'bootstrap' ? { 'X-Bootstrap': '1' } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({}),
    });
  } catch {
    // Network error, CORS, DNS, aborted — all transient, do not log the user out
    return 'transient';
  }

  if (response.ok) {
    const data: RefreshResponse = await response.json();
    tokenStore.setAccessToken(data.accessToken);
    return 'success';
  }

  // Only 401/403 indicate an actually-invalid refresh token. Anything else —
  // 5xx, 429, 502/503/504 during cold start — is transient and must not
  // trigger a logout.
  if (response.status === 401 || response.status === 403) {
    return 'auth-failed';
  }

  return 'transient';
}

async function doRefresh(mode: 'bootstrap' | 'rotate'): Promise<RefreshResult> {
  for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
    const result = await attemptSingleRefresh(mode);

    if (result !== 'transient') {
      return result;
    }

    if (attempt < REFRESH_RETRY_DELAYS_MS.length) {
      await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAYS_MS[attempt]));
    }
  }

  return 'transient';
}

async function attemptTokenRefresh(mode: 'bootstrap' | 'rotate' = 'rotate'): Promise<RefreshResult> {
  // Impersonation tokens are intentionally non-refreshable (P5). No refresh
  // cookie was set when admin-api minted the token, so hitting /auth/refresh
  // would actually log the impersonator OUT of their normal session via the
  // shared tt_refresh cookie. Treat expiry as a deliberate end-of-session.
  if (tokenStore.getImpersonationClaims() !== null) {
    return 'auth-failed';
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doRefresh(mode);
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Bootstrap the in-memory access token from the HttpOnly `tt_refresh` cookie
 * exactly once per page load (security issue #157).
 *
 * The access token now only lives in memory, so a page reload starts with no
 * token. Without bootstrap the first `/current-user` request goes out without
 * an Authorization header and the API returns the unauthenticated `null`
 * shape — the SPA then thinks the user is anonymous and bounces them to
 * /login on every reload.
 *
 * Done lazily inside `apiFetcher` (rather than eagerly at app start) so tests
 * with primed query caches never need to mock the refresh flow.
 */
let bootstrapPromise: Promise<void> | null = null;

function ensureAuthBootstrap(): Promise<void> {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }
  if (tokenStore.getAccessToken()) {
    bootstrapPromise = Promise.resolve();
    return bootstrapPromise;
  }
  bootstrapPromise = attemptTokenRefresh('bootstrap').then(() => undefined);
  return bootstrapPromise;
}

/** Tests reset the memoised bootstrap promise between cases. */
export function _resetAuthBootstrapForTests(): void {
  bootstrapPromise = null;
}

/**
 * Custom fetcher for Orval v8 generated hooks. Orval calls this with the
 * fetch-style signature `(url, options)` — the options object already has
 * `method`, `headers`, and a stringified `body` (or a `FormData` instance
 * for multipart uploads). We layer on auth, PostHog session id, and 401
 * refresh handling, then return parsed JSON.
 */
export async function apiFetcher<TResponse>(url: string, options: RequestInit = {}): Promise<TResponse> {
  const fullUrl = `${API_BASE}${url}`;

  // Hydrate the in-memory access token from the HttpOnly cookie on the first
  // request after a page load. Subsequent calls hit the memoised promise and
  // skip straight through.
  await ensureAuthBootstrap();

  const buildHeaders = (token: string | null): HeadersInit => {
    const posthogSessionId = posthog.get_session_id();
    const merged = new Headers(options.headers);
    if (token) {
      merged.set('Authorization', `Bearer ${token}`);
    }
    if (posthogSessionId) {
      merged.set('x-posthog-session-id', posthogSessionId);
    }
    return merged;
  };

  const buildInit = (token: string | null): RequestInit => ({
    ...options,
    headers: buildHeaders(token),
    // The HttpOnly tt_refresh cookie needs to ride along for /auth/refresh,
    // /auth/logout, and any future cookie-reading endpoint. CORS is already
    // configured with Access-Control-Allow-Credentials: true on all envs.
    credentials: 'include',
  });

  const response = await fetch(fullUrl, buildInit(tokenStore.getAccessToken()));

  // Auto-refresh on 401 — but never for unauthenticated auth endpoints, where
  // a 401 is the endpoint's own domain error and the caller handles it inline.
  if (response.status === 401 && !isUnauthenticatedPath(url)) {
    const refreshResult = await attemptTokenRefresh();

    if (refreshResult === 'success') {
      const retryResponse = await fetch(fullUrl, buildInit(tokenStore.getAccessToken()));

      if (retryResponse.ok) {
        if (retryResponse.status === 204) {
          return undefined as TResponse;
        }
        return retryResponse.json();
      }

      // Post-refresh retry failed. A 401 here means the brand-new token is
      // already invalid — treat that as a persistent auth failure and log
      // out. Any other status is surfaced as a normal API error so the user
      // stays logged in.
      if (retryResponse.status === 401) {
        tokenStore.clearTokens();
        window.location.href = '/login';
        throw new AuthenticationError();
      }
      const retryErrorBody: ErrorBody = await retryResponse.json().catch(() => ({ message: retryResponse.statusText }));
      throw new ApiErrorResponse(
        retryErrorBody.error || retryErrorBody.message || `API error: ${retryResponse.status}`,
        retryResponse.status
      );
    }

    if (refreshResult === 'transient') {
      // Transient backend failure (cold start, 5xx, network) — preserve
      // tokens so the user stays logged in and can retry. We surface this as
      // a server error rather than clearing the session and redirecting.
      throw new ApiErrorResponse('Authentication service temporarily unavailable');
    }

    // refreshResult === 'auth-failed' — refresh token is genuinely invalid,
    // expired, or revoked. Clear tokens and send the user to /login.
    tokenStore.clearTokens();
    window.location.href = '/login';
    throw new AuthenticationError();
  }

  if (!response.ok) {
    const errorBody: ErrorBody & {
      code?: string;
      pricePerRo?: number;
      allowance?: number;
      used?: number;
      conflictingDiscountIds?: string[];
      reason?: string;
      existingDiscountId?: string;
      requiredTier?: string;
      lastConsentStatus?: NeedsOptInLastConsentStatus;
      granularity?: MatrixAssociationGranularity;
      nodeId?: string;
      nodeName?: string | null;
    } = await response.json().catch(() => ({ message: response.statusText }));

    // Duplicate customer detected (409 with existingCustomer payload)
    if (response.status === 409 && errorBody.existingCustomer) {
      throw new DuplicateCustomerError(errorBody.existingCustomer);
    }

    // SMS-send endpoint rejected because the customer hasn't opted in (409 with
    // code='needs_optin'). The shared error type preserves `lastConsentStatus`
    // so the calling modal can pick copy and offer the "Send opt-in request"
    // CTA inline instead of toasting a generic error.
    if (
      response.status === 409 &&
      errorBody.code === 'needs_optin' &&
      (errorBody.lastConsentStatus === 'never' || errorBody.lastConsentStatus === 'opted_out')
    ) {
      throw new NeedsOptInError(errorBody.lastConsentStatus, errorBody.error ?? errorBody.message);
    }

    // Same coupon already applied to this RO (409 with reason='coupon_already_applied')
    if (response.status === 409 && errorBody.reason === 'coupon_already_applied' && errorBody.existingDiscountId) {
      throw new CouponAlreadyAppliedError(errorBody.existingDiscountId);
    }

    // Discount stacking conflict (409 with conflictingDiscountIds payload)
    if (response.status === 409 && Array.isArray(errorBody.conflictingDiscountIds)) {
      throw new DiscountStackingConflictError(errorBody.conflictingDiscountIds);
    }

    // Pricing-matrix association overlap (409 with code='MATRIX_ASSOCIATION_CONFLICT').
    // Two enabled matrices can't target the same taxonomy node at the same
    // granularity (ADR-0016). The structured node is preserved so the page can
    // name the conflicting category/subcategory/part type in its toast.
    if (
      response.status === 409 &&
      errorBody.code === 'MATRIX_ASSOCIATION_CONFLICT' &&
      typeof errorBody.granularity === 'string' &&
      typeof errorBody.nodeId === 'string'
    ) {
      throw new MatrixAssociationConflictError(
        errorBody.granularity,
        errorBody.nodeId,
        errorBody.nodeName ?? null,
        errorBody.error ?? errorBody.message ?? 'Matrix association conflict'
      );
    }

    // Payment method required (402) for free-plan RO metering gate
    if (response.status === 402 && errorBody.code === 'payment_method_required') {
      throw new PaymentMethodRequiredError({
        pricePerRo: errorBody.pricePerRo ?? 0,
        allowance: errorBody.allowance ?? 0,
        used: errorBody.used ?? 0,
      });
    }

    // Tier upgrade required (402) for actions gated by subscription tier
    // (e.g., Free/Pro orgs adding a second shop). Carries the target tier so
    // the UI can render an upgrade CTA pointing at the right tier.
    if (response.status === 402 && errorBody.code === 'tier_upgrade_required') {
      throw new TierUpgradeRequiredError(errorBody.requiredTier ?? '');
    }

    // Blocked stage transition (422) — backend rejects accounting-illegal
    // PATCH stage moves on repair orders (closed → anything, invoice →
    // earlier) and Phase 2A workflow protections (in_progress → intake,
    // raw stage write to closed, non-approved → in_progress). The structured
    // `code` + `message` are preserved so feature onError handlers can show
    // the backend's exact copy.
    if (
      response.status === 422 &&
      errorBody.error === 'BLOCKED_STAGE_TRANSITION' &&
      typeof errorBody.code === 'string' &&
      BLOCKED_STAGE_TRANSITION_CODES.has(errorBody.code as BlockedStageTransitionCode) &&
      errorBody.message
    ) {
      throw new BlockedStageTransitionError(errorBody.code as BlockedStageTransitionCode, errorBody.message);
    }

    // Blocked sub-stage transition (422) — backend rejects sub-stage moves that
    // violate Phase 2A intake-locking protections (after a customer has
    // approved or declined an estimate, the intake sub-stage is locked) or
    // Phase 4A/4B content invariants (each chip that implies state requires
    // its supporting record: invoice, approved job, DVI, estimate-sent flag,
    // declined job, parts order, addon, sublet, payment).
    if (
      response.status === 422 &&
      errorBody.error === 'BLOCKED_SUB_STAGE_TRANSITION' &&
      typeof errorBody.code === 'string' &&
      BLOCKED_SUB_STAGE_TRANSITION_CODES.has(errorBody.code as BlockedSubStageTransitionCode) &&
      errorBody.message
    ) {
      throw new BlockedSubStageTransitionError(errorBody.code as BlockedSubStageTransitionCode, errorBody.message);
    }

    // RO-not-convertible (422) — backend's advance-workflow and stage-PATCH
    // routes reject `mark_work_complete` and intake/in_progress → invoice
    // moves when approved jobs lack billable content or completed labor. The
    // structured reason (`<stage>/<slug>`) is parsed so the block dialog can
    // surface user-facing copy keyed to the failure mode instead of the raw
    // slug. Drag-drop blocks these client-side via `toInvoiceRule`; this
    // throw covers programmatic paths and stale-cache races.
    if (response.status === 422 && errorBody.error === 'RO_NOT_CONVERTIBLE' && typeof errorBody.message === 'string') {
      throw new RoNotConvertibleError(parseRoNotConvertibleReason(errorBody.message), errorBody.message);
    }

    // Reopen rejection (422) — backend's reopen route uses the `error` field
    // itself as the discriminator (RO_NOT_REOPENABLE, REOPEN_NOT_ALLOWED_PAID,
    // INVALID_REOPEN_TARGET) and carries the human-readable copy in `message`.
    // Surface as a typed error so the dialog's onError handler can toast the
    // backend's exact text instead of the raw discriminator string.
    if (
      response.status === 422 &&
      typeof errorBody.error === 'string' &&
      REOPEN_ERROR_CODES.has(errorBody.error as ReopenErrorCode) &&
      errorBody.message
    ) {
      throw new ReopenError(errorBody.error as ReopenErrorCode, errorBody.message);
    }

    // Validation error with field-level details
    if (errorBody.fields && Object.keys(errorBody.fields).length > 0) {
      throw ValidationError.fromApiError({
        error: errorBody.error || 'Validation failed',
        fields: errorBody.fields,
      });
    }

    throw new ApiErrorResponse(
      errorBody.error || errorBody.message || `API error: ${response.status}`,
      response.status
    );
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const json = await response.json();

  // Lambda crash responses sneak through as 200 with an error body
  if (json && typeof json === 'object' && 'errorType' in json) {
    throw new ApiErrorResponse(json.errorMessage || 'Server error', 200);
  }

  return json;
}
